import React, { useEffect, useMemo } from "react";
import { Spin } from "antd";
import { StarOutlined, StarFilled } from "@ant-design/icons";
import SelectItem from "./SelectItem";
import { useIntl } from "react-intl";
import {
  IListProps,
  IUserResponse,
  SearchStateType,
} from "../../../Orchestrator/Types/Index";
import { getIconForType } from "./GetIconForType";
import Image from "next/image";
import { useRecState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { isValidFmlmMile } from "../../../Actions/UIActions/Search/FavouriteFmlmLocationAction";
import { _eventBus } from "../../../core/events/EventBus";
import { fmlmSearchTree } from "../../../Orchestrator/ConstantTree/FmlmSearch/FmlmSearchTree";
import { LocationState } from "../../../Orchestrator/Types/Types/FmlmSearch/FmlmSearchType";
import { IFmlmFavouriteLocationAtom } from "../../../Orchestrator/Recoils/LocationAtom";
import { hydrateFmlmFavouritesFromLocalStorageToAtom } from "../../../Actions/UIActions/FmlmSearch/SaveFmlmFavouritesToLocalStorageAction";
import stopManager from "../../../Lib/Utils/StopManager";
import { useStopsWhitelist } from "../../../hooks/useStopsWhitelist";

const List: React.FC<IListProps> = ({
  locations,
  favorites,
  onSelect,
  onToggleFavorite,
  onToggleDropdown,
  openDropdowns,
  locationFor,
  isFromPopUp,
}) => {
  const intl = useIntl();

  const [userData] = useRecState<IUserResponse>("userDataAtom");
  // Raw loaded stops — the reliable "are stations loaded yet?" signal. The
  // `locations` prop is a derived/debounced list (updatedStopsAtom) that starts
  // empty and only populates after the dropdown opens, so it must NOT be used to
  // decide whether to show the loading spinner: that made the spinner appear
  // even though stops were fully loaded.
  const [stopsResponse] = useRecState<any[]>("stopsResponseAtom");
  // Second half of the same "are we ready to show a list?" question. The
  // origin/destination list is filtered against the `stops_whitelist` registry
  // key, which fails closed — so between stops landing and the registry landing
  // the parent hands us an empty list that is not an answer yet. Without this
  // the dropdown reads as "no stations" for that window.
  const stopsWhitelist = useStopsWhitelist();
  const [search] = useRecState<SearchStateType>("searchStateAtom");
  const [searchPopupData] = useRecState<SearchStateType>(
    "searchStatePopupAtom"
  );

  const selectedLocationName = useMemo(() => {
    const state = isFromPopUp ? searchPopupData : search;
    if (locationFor === "from") return state?.toName;
    else if (locationFor === "to") return state?.fromName;
  }, [searchPopupData, search, locationFor, isFromPopUp]);

  // Show all stations in both "From" and "To" — including the station selected
  // in the other field. Picking the other field's station clears that field
  // (handled in SelectOriginDestinationAction).
  const filteredLocations = locations;

  const [fmlmFavouriteLocationAtom] = useRecState<IFmlmFavouriteLocationAtom>(
    "fmlmFavouriteLocationAtom"
  );

  const favoriteFmlm = useMemo(() => {
    let currentFmlmMile;
    if (locationFor === "from") {
      currentFmlmMile = fmlmFavouriteLocationAtom?.firstMile;
    }
    if (locationFor === "to") {
      currentFmlmMile = fmlmFavouriteLocationAtom?.lastMile;
    }

    if (currentFmlmMile && isValidFmlmMile(currentFmlmMile)) {
      return currentFmlmMile;
    }
  }, [fmlmFavouriteLocationAtom, locationFor]);

  useEffect(() => {
    if (!userData || !userData.id) {
      hydrateFmlmFavouritesFromLocalStorageToAtom();
    }
  }, []);

  const handleUnfavoriteFmlmLocation = (
    e: React.MouseEvent<HTMLSpanElement>
  ) => {
    e.stopPropagation();

    _eventBus.emit({
      eventName: fmlmSearchTree.Favourite.Location.Click,
      data: {
        userData: userData,
        locationState: null,
        selectedExactLocation:
          locationFor === "from" ? "origin" : "destination",
        intl: intl,
      },
    });
  };

  const handleFmlmLocationClick = () => {
    if (!favoriteFmlm) return false;

    let locationData: LocationState = {
      name: favoriteFmlm.name,
      coordinates: [favoriteFmlm.longitude, favoriteFmlm.latitude],
      id: "",
      place_formatted: "",
      locationType: "location",
    };
    _eventBus.emit({
      eventName: fmlmSearchTree.ConfirmFmlmSearch.Click,
      data: {
        visible: false,
        selectedExactLocation:
          locationFor === "from" ? "origin" : "destination",
        search: search,
        locationData: locationData,
        isFromPopUp: false,
      },
    });
  };

  return (
    <div className="location-overflow">
      {!!favoriteFmlm && favoriteFmlm.name !== selectedLocationName && (
        <div className="location-item" onClick={handleFmlmLocationClick}>
          <span className="align-items-center fw-500 fs-16">
            <Image
              src="/search/outline-location-pin.svg"
              className="mr-8"
              height={20}
              width={18}
              alt="location"
            />
            {favoriteFmlm.name}
          </span>

          <span
            onClick={handleUnfavoriteFmlmLocation}
            className="ml-8 cursor-pointer mr-5"
          >
            <StarFilled className="color-primary h-20 w-20" />
          </span>
        </div>
      )}      
      {(!stopsResponse || stopsResponse.length === 0 || stopsWhitelist === null) && (
        <div className="stations-loading" data-testid="stations-loading">
          <Spin size="small" />
        </div>
      )}
      {filteredLocations.map((location: any, index: number) => (
        <div
          key={index}
          className="location-item-container"
          data-id={`location-container-${location.name}`}
        >
          <SelectItem
            item={location}
            isOpen={openDropdowns[location.name]}
            isFavorite={favorites.includes(location.id)}
            onClick={() => {
              let isShuttle = location.type === "bus";
              onSelect(
                location.id,
                stopManager.getStopNameById(location.id),
                isShuttle,
                location?.geoPosition
              );
            }}
            onToggleFavorite={onToggleFavorite}
            onToggleDropdown={onToggleDropdown}
          />
          {openDropdowns[location.name] && location.subStations.length > 0 && (
            <div className="dropdown-menu">
              {location.subStations.map((shuttle: any, index: number) => (
                <div
                  key={index}
                  className="dropdown-item"
                  onClick={() =>
                    onSelect(
                      shuttle.id,
                      stopManager.getStopNameById(shuttle.id),
                      shuttle.type === "bus",
                      shuttle.geoPosition
                    )
                  }
                  data-id={`substation-item-${stopManager.getStopNameById(
                    shuttle.id
                  )}`}
                >
                  <span>
                    {getIconForType(shuttle.type.toLowerCase())}
                    {/* {intl.formatMessage({ id: shuttle.id, defaultMessage: shuttle.name })} */}
                    {stopManager.getStopNameById(shuttle.id)}
                  </span>
                  <span
                    className="ml-8 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleFavorite(shuttle.id, e);
                    }}
                    data-id={`favorite-icon-${stopManager.getStopNameById(
                      shuttle.id
                    )}`}
                    data-testid={`favorite-icon-${stopManager.getStopNameById(
                      shuttle.id
                    )}`}
                  >
                    {favorites.includes(shuttle.id) ? (
                      <StarFilled className="h-20 w-20 color-primary" />
                    ) : (
                      <StarOutlined className="h-20 w-20 color-757575" />
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default List;
